import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  keycloak,
  logout as keycloakLogout,
  getUserInfo,
  type KeycloakUserInfo,
} from '@/lib/keycloak';

/**
 * Authentication state interface
 */
interface AuthState {
  // State
  isAuthenticated: boolean;
  isInitialized: boolean;
  user: KeycloakUserInfo | null;
  tokenExpiration: number | null;

  // Actions
  setAuthenticated: (authenticated: boolean) => void;
  setInitialized: (initialized: boolean) => void;
  setUser: (user: KeycloakUserInfo | null) => void;
  setTokenExpiration: (expiration: number | null) => void;
  updateFromKeycloak: () => void;
  logout: () => void;
  reset: () => void;
}

/**
 * Initial state values
 */
const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  tokenExpiration: null,
};

/**
 * Auth store with Zustand for centralized authentication state management.
 * Integrates with Keycloak adapter for authentication operations.
 * Uses persist middleware to maintain initialization state across page refreshes.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      ...initialState,

      setAuthenticated: (authenticated) => set({ isAuthenticated: authenticated }),

      setInitialized: (initialized) => set({ isInitialized: initialized }),

      setUser: (user) => set({ user }),

      setTokenExpiration: (expiration) => set({ tokenExpiration: expiration }),

      updateFromKeycloak: () => {
        const user = getUserInfo();
        const tokenExp = keycloak.tokenParsed?.exp ?? null;
        set({
          isAuthenticated: keycloak.authenticated,
          user,
          tokenExpiration: tokenExp,
        });
      },

      logout: () => {
        set(initialState);
        keycloakLogout();
      },

      reset: () => set(initialState),
    }),
    {
      name: 'auth-storage',
      // Don't persist any auth state - it must be fresh each session.
      // Keycloak manages session state via cookies/tokens.
      partialize: () => ({}),
      // Version 1: Invalidate old persisted data that incorrectly stored isInitialized
      version: 1,
      // Return empty state for any version migration - we don't persist anything
      migrate: () => ({}) as Record<string, never>,
    }
  )
);

// Selector hooks for common use cases
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useIsAuthInitialized = () => useAuthStore((state) => state.isInitialized);
